/**
 * Rapier physics wrapper.
 *
 * Two things live here that the rest of the game depends on being right:
 *
 * 1. COLLISION LAYERS. Rapier packs interaction groups into a u32 as
 *    (membership << 16) | filter. Getting this wrong produces baffling bugs
 *    (the cat falling through floors, sight rays stopped by the cat's own
 *    collider), so always go through `interactionGroups()` rather than writing
 *    a hex literal.
 *
 * 2. LINE OF SIGHT. `hasLineOfSight` casts against the SIGHT_BLOCKER layer only.
 *    Walls and tall furniture block vision; rugs, small props and the characters
 *    themselves do not. Granny must never be blinded by a teacup.
 */

import RAPIER from '@dimforge/rapier3d-compat';
import type { Vec3 } from '@/core/math';
import { distance, normalize, sub } from '@/core/math';
import { tuning } from '@/core/tuning';

/** Layer bits. Keep in sync with the table in ARCHITECTURE.md. */
export const Layer = {
  /** Static level geometry: floors, walls, furniture bodies. */
  WORLD: 1 << 0,
  /** Subset of WORLD that blocks vision. Walls + tall furniture. */
  SIGHT_BLOCKER: 1 << 1,
  CAT: 1 << 2,
  GRANNY: 1 << 3,
  /** Dynamic knockable props. */
  PROP: 1 << 4,
  /** Non-colliding trigger volumes (hide spots, drop zones, cat moments). */
  SENSOR: 1 << 5,
  /** The cat's ragdoll bones, while active. */
  RAGDOLL: 1 << 6,
} as const;

export type LayerBit = (typeof Layer)[keyof typeof Layer];

/** Packs Rapier interaction groups. `membership` is what I am, `filter` is what I hit. */
export function interactionGroups(membership: number, filter: number): number {
  return ((membership & 0xffff) << 16) | (filter & 0xffff);
}

/* Common group presets, so callers never hand-roll bitmasks. */
export const Groups = {
  staticWorld: interactionGroups(
    Layer.WORLD,
    Layer.CAT | Layer.GRANNY | Layer.PROP | Layer.RAGDOLL,
  ),
  sightBlocker: interactionGroups(
    Layer.WORLD | Layer.SIGHT_BLOCKER,
    Layer.CAT | Layer.GRANNY | Layer.PROP | Layer.RAGDOLL,
  ),
  cat: interactionGroups(Layer.CAT, Layer.WORLD | Layer.SIGHT_BLOCKER | Layer.PROP),
  granny: interactionGroups(Layer.GRANNY, Layer.WORLD | Layer.SIGHT_BLOCKER | Layer.PROP),
  prop: interactionGroups(
    Layer.PROP,
    Layer.WORLD | Layer.SIGHT_BLOCKER | Layer.PROP | Layer.CAT | Layer.GRANNY | Layer.RAGDOLL,
  ),
  ragdoll: interactionGroups(Layer.RAGDOLL, Layer.WORLD | Layer.SIGHT_BLOCKER | Layer.PROP),
  /** Sensors collide with nothing; they are queried explicitly. */
  sensor: interactionGroups(Layer.SENSOR, 0),
} as const;

/**
 * Ray filter that hits ONLY things which block vision.
 *
 * READ THIS BEFORE TOUCHING ANY RAY FILTER. Rapier reports a hit only when BOTH
 * halves of the interaction test pass:
 *
 *     (ray.membership  & collider.filter)     != 0
 *     (collider.membership & ray.filter)      != 0
 *
 * The obvious-looking `interactionGroups(SIGHT_BLOCKER, SIGHT_BLOCKER)` fails the
 * FIRST condition: walls carry filter CAT|GRANNY|PROP|RAGDOLL, which does not
 * include SIGHT_BLOCKER, so the ray passes straight through every wall in the
 * game. That bug made granny able to see through walls while every unit test
 * still passed, because the sensor tests inject a fake line-of-sight function.
 *
 * So the ray's membership must be ALL bits (it is a query, not a physical
 * object), and only its filter narrows what it can hit. Verified by
 * tests/sim/lineOfSight.test.ts, which uses real geometry.
 */
const ALL_LAYERS = 0xffff;
export const SIGHT_FILTER = interactionGroups(ALL_LAYERS, Layer.SIGHT_BLOCKER);

/** Ray filter for ground probes: any solid static geometry. */
export const GROUND_FILTER = interactionGroups(ALL_LAYERS, Layer.WORLD | Layer.SIGHT_BLOCKER);

let initialized = false;

/** Must be awaited once before any other physics call. */
export async function initPhysics(): Promise<void> {
  if (initialized) return;
  await RAPIER.init();
  initialized = true;
}

export function isPhysicsReady(): boolean {
  return initialized;
}

export class PhysicsWorld {
  readonly world: RAPIER.World;

  /** Steps taken. Also tracks whether the scene-query pipeline has been built. */
  private stepsTaken = 0;

  constructor() {
    if (!initialized) {
      throw new Error('initPhysics() must be awaited before constructing PhysicsWorld');
    }
    this.world = new RAPIER.World({ x: 0, y: tuning.sim.gravity, z: 0 });
    // Lock the solver to our fixed timestep. This is what makes the simulation
    // reproducible across machines and makes tests/sim/ meaningful.
    this.world.timestep = 1 / tuning.sim.fixedHz;
  }

  step(): void {
    this.world.step();
    this.stepsTaken++;
  }

  /**
   * Rapier builds its scene-query structures during `world.step()`, so EVERY ray
   * cast returns nothing until the world has stepped at least once - regardless
   * of filters. On the first frame of a level that silently reported "clear line
   * of sight to everything", which is the worst possible default for a stealth
   * game.
   *
   * This version of the bindings exposes no way to rebuild the query pipeline
   * without stepping, so we take one step on demand. It costs a single 1/60
   * frame, once, at a moment when nothing has any velocity yet.
   *
   * Done lazily rather than as a "remember to call primeQueries() after loading"
   * convention, because that convention would eventually be forgotten and the
   * failure mode is invisible.
   */
  private ensureQueriesReady(): void {
    if (this.stepsTaken > 0) return;
    this.step();
  }

  /** Immovable box. `position` is the box CENTER, matching level geometry. */
  addStaticBox(params: {
    position: Vec3;
    size: Vec3;
    rotationY?: number;
    sightBlocker?: boolean;
  }): RAPIER.Collider {
    const body = this.world.createRigidBody(
      RAPIER.RigidBodyDesc.fixed()
        .setTranslation(params.position.x, params.position.y, params.position.z)
        .setRotation(quaternionFromYaw(params.rotationY ?? 0)),
    );

    const desc = RAPIER.ColliderDesc.cuboid(
      params.size.x / 2,
      params.size.y / 2,
      params.size.z / 2,
    ).setCollisionGroups(params.sightBlocker ? Groups.sightBlocker : Groups.staticWorld);

    return this.world.createCollider(desc, body);
  }

  /** Dynamic knockable prop. `position` is the BASE, matching level prop data. */
  addDynamicBox(params: {
    position: Vec3;
    size: Vec3;
    mass: number;
    rotationY?: number;
    friction?: number;
    restitution?: number;
  }): { body: RAPIER.RigidBody; collider: RAPIER.Collider } {
    const body = this.world.createRigidBody(
      RAPIER.RigidBodyDesc.dynamic()
        // Bodies are centered, so lift by half the height to put the base at y.
        .setTranslation(params.position.x, params.position.y + params.size.y / 2, params.position.z)
        .setRotation(quaternionFromYaw(params.rotationY ?? 0))
        // Without damping, small props jitter forever on the floor and never
        // come to rest, which looks broken and wastes the solver's time.
        .setLinearDamping(0.25)
        .setAngularDamping(0.4),
    );

    const desc = RAPIER.ColliderDesc.cuboid(params.size.x / 2, params.size.y / 2, params.size.z / 2)
      .setCollisionGroups(Groups.prop)
      .setMass(params.mass)
      .setFriction(params.friction ?? 0.7)
      .setRestitution(params.restitution ?? 0.12);

    const collider = this.world.createCollider(desc, body);
    return { body, collider };
  }

  /** Kinematic capsule body for a character (cat or granny). */
  addCharacterCapsule(params: { position: Vec3; radius: number; height: number; isCat: boolean }): {
    body: RAPIER.RigidBody;
    collider: RAPIER.Collider;
  } {
    const halfHeight = Math.max(0.01, params.height / 2 - params.radius);

    const body = this.world.createRigidBody(
      RAPIER.RigidBodyDesc.kinematicPositionBased().setTranslation(
        params.position.x,
        params.position.y + params.height / 2,
        params.position.z,
      ),
    );

    const desc = RAPIER.ColliderDesc.capsule(halfHeight, params.radius).setCollisionGroups(
      params.isCat ? Groups.cat : Groups.granny,
    );

    const collider = this.world.createCollider(desc, body);
    return { body, collider };
  }

  /**
   * Kinematic character controller.
   * `offset` is the skin width kept between the capsule and geometry; too small
   * and the character catches on seams, too large and it floats.
   */
  createCharacterController(offset = 0.02): RAPIER.KinematicCharacterController {
    const controller = this.world.createCharacterController(offset);
    controller.setUp({ x: 0, y: 1, z: 0 });
    controller.setMaxSlopeClimbAngle((tuning.cat.maxSlopeClimbDeg * Math.PI) / 180);
    controller.setMinSlopeSlideAngle((tuning.cat.minSlopeSlideDeg * Math.PI) / 180);
    controller.enableAutostep(tuning.cat.stepHeight, 0.05, true);
    controller.enableSnapToGround(tuning.cat.snapToGroundDistance);
    controller.setApplyImpulsesToDynamicBodies(true);
    // Character mass decides how hard the cat shoves props. The real cat mass
    // makes a vase barely wobble, so this is deliberately exaggerated - a cat
    // that cannot knock things over is not this game.
    controller.setCharacterMass(tuning.cat.mass * 3);
    controller.setSlideEnabled(true);
    return controller;
  }

  /**
   * Line of sight between two points, blocked only by the SIGHT_BLOCKER layer.
   *
   * This is THE function granny's vision depends on. If she starts seeing
   * through walls or being blinded by props, the bug is almost always a
   * collision-group mistake here rather than in the AI.
   */
  hasLineOfSight(from: Vec3, to: Vec3): boolean {
    this.ensureQueriesReady();
    const delta = sub(to, from);
    const dist = distance(from, to);
    if (dist < 1e-4) return true;

    const ray = new RAPIER.Ray(from, normalize(delta));
    const hit = this.world.castRay(ray, dist, true, undefined, SIGHT_FILTER);
    // No hit before the target means nothing solid is in the way.
    return hit === null || hit.timeOfImpact >= dist - 1e-3;
  }

  /** Number of sight blockers between two points. Drives hearing attenuation. */
  countWallsBetween(from: Vec3, to: Vec3, maxWalls: number): number {
    this.ensureQueriesReady();
    const dist = distance(from, to);
    if (dist < 1e-4) return 0;

    const direction = normalize(sub(to, from));

    // Enumerate every intersection in ONE query and count DISTINCT colliders.
    //
    // The obvious implementation - march the ray forward past each hit - is a
    // trap: you have to nudge the origin past the surface, and any nudge smaller
    // than the wall's thickness restarts the ray INSIDE the wall, where
    // `solid: true` reports a hit at toi 0 and the same wall gets counted over
    // and over. A 0.2m wall counted as 3. Wall thickness varies per level, so
    // there is no safe constant nudge.
    const seen = new Set<number>();
    this.world.intersectionsWithRay(
      new RAPIER.Ray(from, direction),
      dist,
      false,
      (intersection) => {
        seen.add(intersection.collider.handle);
        // Stop early once we have hit the attenuation cap - more walls make no
        // audible difference.
        return seen.size < maxWalls;
      },
      undefined,
      SIGHT_FILTER,
    );

    return Math.min(seen.size, maxWalls);
  }

  /**
   * Ground height directly under a point, or undefined if there is nothing below.
   *
   * Also undefined when `fromY` is INSIDE solid geometry. A solid ray that starts
   * inside a shape reports a time-of-impact of zero, so the naive reading of the
   * result is `fromY` itself - the query invents a floor at exactly the height
   * you asked about. That fabricated floor is what once sent the cat's procedural
   * paws climbing 0.3m per gait cycle whenever it walked flush against a wall.
   */
  groundHeightAt(x: number, z: number, fromY = 5): number | undefined {
    this.ensureQueriesReady();
    const hit = this.world.castRay(
      new RAPIER.Ray({ x, y: fromY, z }, { x: 0, y: -1, z: 0 }),
      fromY + 5,
      true,
      undefined,
      GROUND_FILTER,
    );
    if (!hit || hit.timeOfImpact <= 0) return undefined;
    return fromY - hit.timeOfImpact;
  }

  free(): void {
    this.world.free();
  }
}

/** Quaternion for a yaw-only rotation. */
export function quaternionFromYaw(yaw: number): { x: number; y: number; z: number; w: number } {
  return { x: 0, y: Math.sin(yaw / 2), z: 0, w: Math.cos(yaw / 2) };
}

/** Re-exported so callers do not each need to import the Rapier namespace. */
export { RAPIER };
